import { useState, useEffect } from "react";
import { useLostItemActions } from "@/hooks/useLostItemActions";
import { useIsMobile } from "@/hooks/use-mobile";
import { useNavigate } from "react-router-dom";
import { useSupabaseAuth } from "@/contexts/SupabaseAuthContext";
import { useLostFoundModals } from "./useLostFoundModals";

export const useLostFoundPage = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [location, setLocationState] = useState("");
  const [status, setStatus] = useState("");
  const [view, setView] = useState<"list" | "grid">("list");
  
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { user } = useSupabaseAuth();
  const itemActions = useLostItemActions();
  const modals = useLostFoundModals();
  
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 768) {
        setView("grid");
      } else {
        setView("list");
      }
    };
    handleResize(); // set initial
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleSearch = (query: string) => {
    setSearchQuery(query);
  };

  const handleLocationChange = (loc: string) => {
    setLocationState(loc);
  };

  const handleStatusChange = (status: string) => {
    setStatus(status);
  };

  const handleResetFilters = () => {
    setSearchQuery("");
    setLocationState("");
    setStatus("");
  };

  const handleToggleView = (newView: "list" | "grid") => {
    setView(newView);
  };
  
  const handleEdit = (item: any) => {
    modals.setSelectedItem(item);
    modals.setEditItemOpen(true);
  };
  
  const openTimeline = (item: any) => {
    modals.setSelectedItem(item);
    modals.setTimelineModalOpen(true);
  };
  
  const handleOpenInfo = (item: any) => {
    modals.setSelectedItem(item);
    modals.setInfoModalOpen(true);
  };
  
  const handleDeliver = (item: any, receiver: string) => {
    itemActions.handleDeliver(item, receiver);
    modals.setSignatureModalOpen(false);
  };
  
  const handleUndo = (item: any) => {
    itemActions.handleUndo(item);
  };
  
  const handleDelete = (item: any) => {
    itemActions.handleDelete(item);
  };
  
  const handleDiscard = (item: any) => {
    itemActions.handleDiscard(item);
  };
  
  const handleConfirmSignature = (item: any, receiver: string) => {
    handleDeliver(item, receiver);
    modals.setSignatureModalOpen(false);
  };
  
  const confirmDelete = () => {
    if (modals.selectedItem) {
      handleDelete(modals.selectedItem);
    }
    modals.setDeleteDialogOpen(false);
  };
  
  const confirmDiscard = () => {
    if (modals.selectedItem) {
      handleDiscard(modals.selectedItem);
    }
    modals.setDiscardDialogOpen(false);
  };

  return {
    searchQuery,
    location,
    status,
    view,
    isMobile,
    navigate,
    user,
    handleSearch,
    handleLocationChange,
    handleStatusChange,
    handleResetFilters,
    handleToggleView,
    toggleView: () => setView(view === 'list' ? 'grid' : 'list'),
    handleDeliver,
    handleUndo,
    handleEdit,
    handleDelete,
    handleDiscard,
    openTimeline,
    handleOpenInfo,
    handleConfirmSignature,
    confirmDelete,
    confirmDiscard,
    ...modals
  };
};
